import { TranslateService } from '@ngx-translate/core';
import { Extension } from "ng2-adsk-forge-viewer";
import { BehaviorSubject } from 'rxjs';
import { UnitShape } from 'src/app/classes/viewer/shapes/unit';
import { defaultModel, Model } from 'src/app/interfaces/api/model';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { ModelService } from 'src/app/services/api/models/model.service';
import { Config } from '../../../interfaces/shared/config';
import { TrackerService } from '../../../services/api/trackers/tracker.service';
import { Cursor } from '../Cursor';
import { ElementDockingPanel } from './element-docking-panel';

declare const Autodesk: any;

interface ExtendedViewer extends Autodesk.Viewing.Viewer3D {
    toolController: any;
    toolbar: any;
    container: any;
    impl: any;
}

export class ElementInfoExtension extends Extension {
    private subToolbar: any;
    private dockingPanel: ElementDockingPanel | null = null;
    public translate: TranslateService | null = null;
    public modelService: ModelService | null = null;
    public trackerService: TrackerService | null = null;
    public model: Model = defaultModel;
    public origin: any;

    units$!: BehaviorSubject<UnitShape[]>;

    callback: any;
    panel = null;
    colors = Config.colors;
    objects: any[] = [];
    public units: UnitShape[] = [];
    public appEvent: any;
    cursor: Cursor | null = null;

    public unitExtension: any = null;
    private unitsInitialized = false;

    protected declare viewer: ExtendedViewer;

    // Extension must have a name
    public static override extensionName: string = 'ElementInfoExtension';
    private cursorSubscription: any;

    constructor(viewer: any, options: any) {
        super(viewer, options);
    }

    public activate(): any {
        // Called when Forge Viewer loads your extension
        return true;
    }

    public deactivate(): any {
        // Called when Forge Viewer loads your extension
        return true;
    }

    public load(): boolean {
        if (!this.viewer) {
            console.error('ElementInfoExtension: viewer is null in load()');
            return false;
        }

        try {
            if (typeof Autodesk === 'undefined') {
                console.error('ElementInfoExtension: Autodesk is undefined');
                return false;
            }

            if (!Autodesk.Viewing) {
                console.error('ElementInfoExtension: Autodesk.Viewing not available');
                return false;
            }

            if (!Autodesk.Viewing.UI) {
                console.error('ElementInfoExtension: Autodesk.Viewing.UI not available');
                return false;
            }


            if (this.viewer.impl && this.viewer.toolbar) {
                this.createUI();
            } else {
                this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, () => {
                    this.createUI();
                });
            }

            return true;
        } catch (error) {
            console.error('ElementInfoExtension: Error in load():', error);
            return false;
        }
    }

    public createUI() {
        if (!this.viewer || !this.viewer.toolbar) {
            console.error('ElementInfoExtension: createUI called but viewer or toolbar is not available');
            return;
        }

        try {
            if (!Autodesk?.Viewing?.UI?.Button) {
                console.error('ElementInfoExtension: Autodesk.Viewing.UI.Button not available');
                return;
            }

            // create toolbar buttons
            const button = new Autodesk.Viewing.UI.Button('info-btn');

            button.onClick = () => {
                this.dockPanel();
            };

            button.addClass('info-btn');
            button.setToolTip('info');
            button.setIcon('info-btn-icon');

            button.onMouseOver = () => {
                button.setIcon('hover-info-btn-icon');
            }

            button.onMouseOut = () => {
                button.setIcon('info-btn-icon');
            }

            this.subToolbar = this.viewer.toolbar.getControl('unifield-toolbar');
            if (!this.subToolbar) {
                this.subToolbar = new Autodesk.Viewing.UI.ControlGroup('unifield-toolbar');
                this.viewer.toolbar.addControl(this.subToolbar);
            }

            this.subToolbar.addControl(button);

            const styleId = 'element-info-extension-style';

            if (!document.getElementById(styleId)) {
                const link = document.createElement('link');
                link.id = styleId;
                link.rel = 'stylesheet';
                link.href = 'viewer/elementInfo/elementInfo-extension/elementInfo-extension.css';
                document.head.appendChild(link);
            }
        } catch (error) {
            console.error('ElementInfoExtension: Error in createUI:', error);
        }
    }

    public init(): void {
        if (!this.translate) {
            console.warn('ElementInfoExtension: translate service not available');
            return;
        }

        if (!this.modelService) {
            console.warn('ElementInfoExtension: modelService not available');
            return;
        }

        if (!this.trackerService) {
            console.warn('ElementInfoExtension: trackerService not available');
            return;
        }

        this.units$.subscribe({
            next: (units: UnitShape[]) => {
                this.units = units;
                this.enableCursor();
            },
            error: (err: any) => {
                console.error('ElementInfoExtension: Error subscribing to units$', err);
            }
        })
    }

    public onUnitsInitialized(units: UnitShape[]): void {
        if (this.units instanceof BehaviorSubject) {
            this.units.next(units);
        }

        this.unitsInitialized = true;
        this.enableCursor();
    }

    public unload(): any {
        try {
            this.objects = [];

            if (this.subToolbar) {
                this.subToolbar.removeControl('info-btn');
                if (this.viewer?.toolbar) {
                    this.viewer.toolbar.removeControl(this.subToolbar);
                }
                this.subToolbar = null;
            }

            if (this.cursor) {
                if (this.cursorSubscription) {
                    this.cursorSubscription.unsubscribe();
                    this.cursorSubscription = null;
                }

                if (this.viewer?.toolController) {
                    this.viewer.toolController.deregisterTool(this.cursor);
                }

                this.cursor = null;
            }

            if (this.dockingPanel) {
                this.dockingPanel.destroy();
                this.dockingPanel = null;
            }

            return true;
        } catch (error) {
            console.error('ElementInfoExtension: Error in unload:', error);
            return false;
        }
    }

    enableCursor(): void {
        if (!this.viewer) {
            console.error('❌ ElementInfoExtension: viewer is null in enableCursor');
            return;
        }

        if (!this.unitsInitialized) {
            return;
        }

        try {
            if (this.cursor) {
                if (this.cursorSubscription) {
                    this.cursorSubscription.unsubscribe();
                    this.cursorSubscription = null;
                }

                if (this.viewer?.toolController) {
                    this.viewer.toolController.deregisterTool(this.cursor);
                }

                this.cursor = null;
            }

            if (!this.units || this.units.length === 0) {
                console.warn('⚠️ ElementInfoExtension: No units available for cursor');
                return;
            }

            const validUnits = this.units.filter(unit => {
                if (!unit || !unit.mesh) {
                    console.warn('⚠️ Unit skipped: no mesh', unit?.name);
                    return false;
                }

                const mesh = unit.mesh;

                if (!mesh.geometry) {
                    console.warn('⚠️ Unit mesh skipped: no geometry', unit.name);
                    return false;
                }

                if (mesh.geometry.type === 'BufferGeometry') {
                    // const position = mesh.geometry.attributes?.position;
                    // if (!position || !position.array || position.array.length === 0) {
                    //     console.warn('⚠️ Unit mesh skipped: invalid position attribute', unit.name);
                    //     return false;
                    // }
                }

                if (!mesh.material) {
                    console.warn('⚠️ Unit mesh skipped: no material', unit.name);
                    return false;
                }

                return true;
            });

            if (validUnits.length === 0) {
                console.warn('⚠️ ElementInfoExtension: No valid units with proper meshes found');
                return;
            }

            const cursorOptions = {
                objects: validUnits,
                viewer: this.viewer
            };

            this.cursor = new Cursor(cursorOptions);

            this.viewer.toolController.registerTool(this.cursor);
            this.viewer.toolController.activateTool(this.cursor.toolName);

            this.cursorSubscription = this.cursor.selection.subscribe(selections => {
                if (selections && Array.isArray(selections) && selections.length > 0) {
                    this.getUnit(selections);
                }
            });
        } catch (error) {
            console.error('💥 ElementInfoExtension: Error enabling cursor:', error);
        }
    }

    getUnit(selections: UnitShape[]) {
        const units: UnitShape[] = [];

        selections.forEach(s => {
            const unit = this.units.find((u: UnitShape) => {
                if (u.trackers && u.trackers.length && u.trackers[0] && u.trackers[0].name === s.name) {
                    return true;
                }
                return false;
            });

            if (!unit) return;

            const unitIndex = units.findIndex((u: UnitShape) => unit && u.trackers[0] && u.trackers[0].name === unit.name);

            if (unit && unitIndex < 0) units.push(unit);
        });

        if (units[0]) {
            this.buildContent(units[0]);
        }
    }

    buildContent(unit: { name: any; trackers: Tracker[] }) {
        if (!unit) return;

        let mapping: { [key: string]: any } = {
            unit: unit.name,
            tracker: unit.trackers && unit.trackers[0] ? unit.trackers[0].name : undefined
        };

        let lastTrack = unit.trackers && unit.trackers[0] ? unit.trackers[0].lastTrack : undefined;

        if (lastTrack) {
            let track = lastTrack;

            if (track.lat != undefined) mapping['Lat'] = track.lat;
            if (track.lon != undefined) mapping['Lon'] = track.lon;
            if (track.x != undefined) mapping['X'] = track.x;
            if (track.y != undefined) mapping['Y'] = track.y;
            if (track.z != undefined) mapping['Z'] = track.z;
            if (track.createdDate != undefined) mapping['Created Date'] = track.createdDate;

            if (track.message)
                Object.keys(track.message).forEach(k => {
                    mapping[k] = track.message[k];
                })
        }

        if (this.appEvent) {
            this.appEvent.emit('viewer_unit_clicked', mapping);
        }
    }

    dockPanel(): void {
        try {
            if (!this.viewer || !this.viewer.container) {
                console.error('ElementInfoExtension: Viewer or container not available');
                return;
            }

            if (typeof Autodesk === 'undefined' || !Autodesk.Viewing) {
                console.error('ElementInfoExtension: Autodesk.Viewing not available');
                return;
            }

            if (!this.dockingPanel) {
                this.dockingPanel = new ElementDockingPanel(
                    this.viewer,
                    this.viewer.container,
                    'elementcontainer',
                    'Element Information',
                    {
                        modelService: this.modelService,
                        trackerService: this.trackerService,
                        translate: this.translate,
                        model: this.model,
                        units: this.units || []
                    }
                );
            }

            const titleDiv = this.viewer.container.querySelector('.docking-panel-title[data-i18n="Element Information"]');

            if (titleDiv && !titleDiv.textContent?.trim()) {
                titleDiv.textContent = "Element Information";
            }

            this.dockingPanel.init();
            this.dockingPanel['setVisible'](true);
        } catch (error) {
            console.error('ElementInfoExtension: Error in dockPanel:', error);
        }
    }
}
